import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PMOC, PMOCCreateInput, PaginatedResponse } from '../types'
import type { PDFv2Flags } from '../types/pmocPlanos'

interface PmocListParams {
  status?: string
  site?: string
  page?: number
  search?: string
}

export function usePmocList(params?: PmocListParams) {
  return useQuery({
    queryKey: ['pmoc', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PMOC>>('/pmoc/', { params })
      return r.data
    },
    staleTime: 1000 * 30,
  })
}

export function usePmocDetalhe(id: string) {
  return useQuery({
    queryKey: ['pmoc', id],
    queryFn: async () => {
      const r = await api.get<PMOC>(`/pmoc/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

function limpaPayload(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  Object.entries(input).forEach(([k, v]) => {
    if (v === '' || v === undefined) return
    out[k] = v
  })
  return out
}

export function usePmocCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PMOCCreateInput) => {
      const r = await api.post<PMOC>(
        '/pmoc/',
        limpaPayload(data as unknown as Record<string, unknown>),
      )
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pmoc'] }),
  })
}

export function usePmocUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PMOCCreateInput>) => {
      const r = await api.patch<PMOC>(
        `/pmoc/${id}/`,
        limpaPayload(data as Record<string, unknown>),
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pmoc'] })
      qc.invalidateQueries({ queryKey: ['pmoc', id] })
    },
  })
}

export function usePmocDuplicar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<PMOC>(`/pmoc/${id}/duplicar/`)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pmoc'] }),
  })
}

export function usePmocAtivar(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.patch<PMOC>(`/pmoc/${id}/`, { status: 'ativo' })
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pmoc'] })
      qc.invalidateQueries({ queryKey: ['pmoc', id] })
    },
  })
}

// ──────────── Planos de Manutenção (sub-recurso de PMOC) ────────────

export interface PlanoManutencao {
  id: string
  pmoc: string
  equipamento: string | null
  equipamento_str?: string
  tipo_os: string
  descricao: string
  periodicidade: string
  dia_semana?: number | null
  dia_mes?: number | null
  proxima_execucao?: string | null
  ativo: boolean
}

export interface PlanoCreateInput {
  equipamento?: string | null
  tipo_os: string
  descricao: string
  periodicidade: string
  dia_semana?: number | null
  dia_mes?: number | null
}

export function usePlanoList(pmocId: string) {
  return useQuery({
    queryKey: ['planos', pmocId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PlanoManutencao>>(
        `/pmoc/${pmocId}/planos/`,
      )
      return r.data
    },
    enabled: !!pmocId,
  })
}

export function usePlanoCreate(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PlanoCreateInput) => {
      const payload = limpaPayload(data as unknown as Record<string, unknown>)
      const r = await api.post<PlanoManutencao>(`/pmoc/${pmocId}/planos/`, payload)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos', pmocId] })
      qc.invalidateQueries({ queryKey: ['pmoc', pmocId] })
    },
  })
}

export interface CatalogoPadraoItem {
  tipo_os: string
  periodicidade: string
  descricao: string
}
export interface CatalogoPadraoResponse {
  planos_por_tipo: Record<string, CatalogoPadraoItem[]>
}

export function useCatalogoPlanosPadrao() {
  return useQuery({
    queryKey: ['pmoc', 'catalogo-padrao'],
    queryFn: async () => {
      const r = await api.get<CatalogoPadraoResponse>('/pmoc/planos/catalogo-padrao/')
      return r.data
    },
    staleTime: 1000 * 60 * 60, // catálogo é estático
  })
}

export interface PlanoImportLinha {
  linha: number
  acao: 'criar' | 'ignorar_duplicado' | 'erro'
  erro: string | null
  dados: Record<string, unknown>
}

export interface PlanoImportResultado {
  total_linhas: number
  a_criar: number
  duplicados: number
  erros: number
  linhas: PlanoImportLinha[]
  mapeamento_colunas: Array<{
    coluna: number
    header_planilha: string
    campo_sistema: string | null
    reconhecida: boolean
  }>
  dry_run: boolean
  commit?: { criados: number; ids: string[] }
}

export function usePlanoImportar(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, dry_run }: { arquivo: File; dry_run: boolean }) => {
      const fd = new FormData()
      fd.append('arquivo', arquivo)
      fd.append('dry_run', dry_run ? 'true' : 'false')
      const r = await api.post<PlanoImportResultado>(
        `/pmoc/${pmocId}/planos/importar/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return r.data
    },
    onSuccess: (data) => {
      if (!data.dry_run) {
        qc.invalidateQueries({ queryKey: ['planos', pmocId] })
        qc.invalidateQueries({ queryKey: ['pmoc', pmocId] })
      }
    },
  })
}

export function downloadTemplatePlanos() {
  return api
    .get('/pmoc/planos/importar/template/', { responseType: 'blob' })
    .then((r) => {
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'template-planos-pmoc123.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    })
}

export function useAplicarPlanosPadrao(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { tipos_equipamento: string[]; incluir_geral: boolean }) => {
      const r = await api.post<{
        total_criados: number
        detalhes: Array<{ tipo_equipamento: string; criados: number; descricoes: string[] }>
      }>(`/pmoc/${pmocId}/planos/aplicar-padrao/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos', pmocId] })
      qc.invalidateQueries({ queryKey: ['pmoc', pmocId] })
    },
  })
}

export function usePlanoDelete(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (planoId: string) => api.delete(`/planos-manutencao/${planoId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos', pmocId] }),
  })
}

export function usePmocExportarPdf(id: string) {
  return useMutation({
    mutationFn: async () => {
      const r = await api.get(`/pmoc/${id}/exportar-pdf/`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pmoc-${id.slice(0, 8)}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}

export function usePmocGerarPdfV2(id: string) {
  return useMutation({
    mutationFn: async (flags: PDFv2Flags = {}) => {
      const params: Record<string, string> = {}
      Object.entries(flags).forEach(([k, v]) => {
        if (v !== undefined) params[k] = v ? '1' : '0'
      })
      const r = await api.get(`/pmoc/${id}/pdf/`, {
        responseType: 'blob',
        params,
      })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pmoc-v2-${id.slice(0, 8)}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}
